import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { clearJBrowseAssetCache } from '../services/jbrowseAssetCache';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const navigate = useNavigate();
  const sessionCheck = useRef(0);

  const clearSession = useCallback(() => {
    clearJBrowseAssetCache();
    localStorage.removeItem('auth_token');
    localStorage.removeItem('username');
    setCurrentUser(null);
    setIsAuthenticated(false);
  }, []);

  const retryAuth = useCallback(async () => {
    const checkId = ++sessionCheck.current;
    setAuthError('');
    setLoading(true);
    try {
      if (!localStorage.getItem('auth_token')) {
        clearSession();
        return false;
      }
      const response = await apiService.client.get('/api/user/');
      if (checkId !== sessionCheck.current) return false;
      setCurrentUser(response.data);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      if (checkId !== sessionCheck.current) return false;
      if ([401, 403].includes(error.response?.status)) {
        clearSession();
      } else {
        // Keep the token for a retry, but do not invent capabilities from a stored username.
        setAuthError('Your sign-in is saved, but we could not check your access. Please retry.');
      }
      return false;
    } finally {
      if (checkId === sessionCheck.current) setLoading(false);
    }
  }, [clearSession]);

  useEffect(() => {
    retryAuth();
    return () => {
      sessionCheck.current += 1;
    };
  }, [retryAuth]);

  const completeLogin = (token, username) => {
    clearJBrowseAssetCache();
    localStorage.setItem('auth_token', token);
    localStorage.setItem('username', username);
    return retryAuth();
  };

  const login = async (username, password) => {
    const response = await apiService.auth.login(username, password);
    return completeLogin(response.data.token, username);
  };

  const logout = () => {
    sessionCheck.current += 1;
    setLoading(false);
    clearSession();
    setAuthError('');
    navigate('/');
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        loading,
        authError,
        retryAuth,
        login,
        completeLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
